import DocsCallout from '../DocsCallout';
import { DocsTable } from '../DocsCards';
import DocsCodeBlock from '../DocsCodeBlock';
import DocsSteps, { DocsStep } from '../DocsSteps';

import { CHATCO_MCP_SCOPE, CHATCO_MCP_URL } from './connector-hosts';
import { EXAMPLE_EXPERT } from './ConnectorSections';

/*
 * Tool names, argument names, limits and result fields mirror TOOL_DEFINITIONS
 * in apps/api/src/mcp-gateway/mcp-gateway.controller.ts and the response types
 * in mcp-gateway.types.ts / mcp-consult.service.ts / mcp-source.service.ts.
 * Examples show only what clients receive after mcpToolResult (mcp-response.ts)
 * strips internal fields such as charged_credits. The retry rules follow
 * McpBillingService (mcp-billing.service.ts). Change them together.
 */

const CONSULT_ARGS = `{
  "expert_id": "<id from list_authorized_experts>",
  "question": "How many vacation days does a new employee get? Cite the sources.",
  "operation_id": "7f3b2c1e-5a9d-4e6f-8b0a-2d4c6e8f1a3b"
}`;

const CONSULT_RESULT = `{
  "conversation_id": "<keep for follow-ups>",
  "answer": "New employees receive … [1]",
  "citations": [
    {
      "id": "<source id>",
      "title": "Employee handbook",
      "url": "<source link>"
    }
  ]
}`;

const FOLLOW_UP_ARGS = `{
  "expert_id": "<same expert>",
  "conversation_id": "<conversation_id from the first answer>",
  "question": "Does that change after two years of service?",
  "operation_id": "c41e9d27-0b6a-4f38-9e15-7a2d8c3b6f90"
}`;

const FETCH_ARGS = `{
  "id": "<source id from the citation>",
  "title": "Employee handbook"
}`;

/**
 * Optional protocol detail for developers and for AI agents reading the docs.
 * Users never need it: every host drives these tools from plain-language requests.
 */
const AgentProtocolReference: React.FC = () => (
  <>
    <p>
      You don&apos;t need this section to use Chat.co from an AI app. It documents the protocol for
      developers and for agents that call the tools directly.
    </p>

    <h3 id="protocol-server">Server</h3>
    <DocsTable
      headers={['Property', 'Value']}
      monoFirstColumn
      rows={[
        ['endpoint', <code key="e">{CHATCO_MCP_URL}</code>],
        [
          'transport',
          'MCP Streamable HTTP, stateless. POST only; GET and DELETE return 405 because there is no session to resume.',
        ],
        [
          'auth',
          <>
            OAuth authorization code with PKCE and dynamic client registration. Discovery starts
            from <code>https://mcp.chat.co/.well-known/oauth-protected-resource</code>, which the
            server also advertises in its <code>401</code> response.
          </>,
        ],
        ['scope', <code key="s">{CHATCO_MCP_SCOPE}</code>],
        [
          'authorization',
          'Re-checked on every call: current workspace membership, expert access, licence and credits.',
        ],
      ]}
    />

    <h3 id="protocol-tools">Tools</h3>
    <DocsTable
      headers={['Tool', 'Arguments', 'Returns', 'Cost']}
      monoFirstColumn
      rows={[
        [
          'list_authorized_experts',
          'None',
          <>
            <code>experts[]</code> with <code>id</code>, <code>name</code>, <code>description</code>
            , <code>access.chat</code> and <code>access.knowledgeSearch</code>
          </>,
          'Free',
        ],
        [
          'consult_expert',
          <>
            <code>expert_id</code>*, <code>question</code>* (≤16,000 chars),{' '}
            <code>operation_id</code>* (16–128 letters, digits, <code>-</code> or <code>_</code>),{' '}
            <code>conversation_id</code>
          </>,
          <>
            <code>conversation_id</code>, <code>answer</code>, <code>citations[]</code> (
            <code>id</code>, <code>title</code>, <code>url</code>), and <code>files[]</code> (same
            fields) only when a requested expert action produced an assessment or file
          </>,
          'Expert’s normal rate, once',
        ],
        [
          'search',
          <>
            <code>query</code>* (≤8,000 chars), <code>operation_id</code>*, <code>expert_id</code>,{' '}
            <code>paying_expert_id</code>
          </>,
          <>
            <code>results[]</code> (<code>id</code>, <code>title</code>, <code>url</code>)
          </>,
          '0.5 credit per completed search',
        ],
        [
          'fetch',
          <>
            <code>id</code>*, <code>title</code>
          </>,
          <>
            <code>id</code>, <code>title</code>, <code>text</code> (bounded excerpt),{' '}
            <code>url</code>, <code>metadata</code> (<code>expert</code>, page range when known)
          </>,
          'Free',
        ],
      ]}
    />
    <p className="text-sm text-slate-500">* Required.</p>

    <h3 id="protocol-recipe">Recipe for agents</h3>
    <DocsSteps>
      <DocsStep title="List experts first">
        <p>
          Call <code>list_authorized_experts</code>. Only use <code>id</code> values it returns.
          Offer <code>search</code> only for experts whose <code>access.knowledgeSearch</code> is{' '}
          <code>true</code>.
        </p>
      </DocsStep>
      <DocsStep title="Consult with a fresh operation_id">
        <p>
          Generate a new identifier (a UUID works) for each new question. Keep the returned{' '}
          <code>conversation_id</code>.
        </p>
        <DocsCodeBlock code={CONSULT_ARGS} language="json" filename="consult_expert arguments" />
        <DocsCodeBlock code={CONSULT_RESULT} language="json" filename="result (abridged)" />
      </DocsStep>
      <DocsStep title="Follow up in the same conversation">
        <p>
          Pass the <code>conversation_id</code> back with a new <code>operation_id</code>.
        </p>
        <DocsCodeBlock code={FOLLOW_UP_ARGS} language="json" filename="consult_expert arguments" />
      </DocsStep>
      <DocsStep title="Read evidence with fetch">
        <p>
          Pass the citation&apos;s <code>id</code> and its <code>title</code>. The title is shown in
          approval prompts; the id is the lookup key. The excerpt may not be the whole document.
        </p>
        <DocsCodeBlock code={FETCH_ARGS} language="json" filename="fetch arguments" />
      </DocsStep>
      <DocsStep title="Present the answer with its links">
        <p>
          Show every cited source as a link to its <code>url</code>. Offer entries under{' '}
          <code>files</code> as downloads, not as citations. Don&apos;t add credit rates, model
          names or internal configuration to the answer.
        </p>
      </DocsStep>
    </DocsSteps>

    <DocsCallout variant="tip" title="Retries and identifiers">
      <p>
        A paid call waits about 25 seconds for its result. If the expert is still working, the tool
        returns a &ldquo;still running&rdquo; error. Repeat the identical call with the{' '}
        <strong>same</strong> <code>operation_id</code> until the result arrives, without starting a
        new question. Do the same when your own request times out or the connection drops before a
        reply. A completed operation returns its stored result and is never charged twice.
      </p>
      <p>
        If the server says the operation <strong>failed</strong> or <strong>expired</strong>{' '}
        (including &ldquo;The expert took too long&rdquo;), it was not charged; to try again, send
        the request with a <strong>new</strong> <code>operation_id</code>. An{' '}
        <code>operation_id</code> reused with different arguments is rejected as a conflict.
      </p>
      <p>
        <code>search</code> without <code>expert_id</code> covers at most three experts and needs{' '}
        <code>paying_expert_id</code>; the results are not exhaustive. Prefer a targeted search.
      </p>
    </DocsCallout>
    <p className="text-sm text-slate-500">
      Synthetic example: the <em>{EXAMPLE_EXPERT}</em> expert, its answer and the angle-bracket
      values are placeholders. Use the identifiers and source links the server actually returns.
    </p>
  </>
);

export default AgentProtocolReference;
